import { FC } from 'react';
import { Badge, Box, Button, InputAdornment, TextField, useTheme } from '@mui/material';
import { ShoppingCartOutlined, SearchOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

type Props = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearchSubmit: () => void;
  cartItemCount: number;
  onCartClick: () => void;
  cartDisabled?: boolean;
};

const StorefrontHeader: FC<Props> = ({
  searchValue,
  onSearchChange,
  onSearchSubmit,
  cartItemCount,
  onCartClick,
  cartDisabled,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('storefront');

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', gap: '12px', height: 44 }}>
      <TextField
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && onSearchSubmit()}
        placeholder={t('searchForAnything')}
        size="small"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SearchOutlined
                sx={{ color: theme.palette.custom.gray[400], cursor: 'pointer', fontSize: 20 }}
                onClick={onSearchSubmit}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          flex: 1,
          marginBottom: 0,
          '& .MuiOutlinedInput-root': {
            borderRadius: '5px',
            backgroundColor: theme.palette.background.paper,
            height: '100%',
            fontSize: '14px',
            transition: 'box-shadow 0.2s ease',
            '&:hover': {
              boxShadow: '0 0 4px 0 rgba(0, 0, 0, 0.10)',
            },
            '& input': {
              padding: '10px 14px',
              boxSizing: 'border-box',
              height: '100%',
            },
            '& fieldset': {
              borderColor: theme.palette.custom.gray[200],
            },
            '&:hover fieldset': {
              borderColor: theme.palette.custom.gray[200],
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.custom.gray[200],
              borderWidth: '1px',
            },
          },
        }}
      />

      <Button
        startIcon={(
          <Badge
            badgeContent={cartItemCount}
            color="error"
            overlap="circular"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '10px',
                minWidth: 16,
                height: 16,
                px: '4px',
                fontWeight: 700,
              },
            }}
          >
            <ShoppingCartOutlined sx={{ fontSize: '18px !important' }} />
          </Badge>
        )}
        onClick={onCartClick}
        disabled={cartDisabled}
        sx={{
          borderRadius: '5px',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '14px',
          px: '20px',
          height: '100%',
          whiteSpace: 'nowrap',
          minWidth: 'auto',
          backgroundColor: '#1B4332',
          color: '#fff',
          '&:hover': {
            backgroundColor: '#143728',
          },
        }}
      >
        {cartItemCount} {t('items')}
      </Button>
    </Box>
  );
};

export default StorefrontHeader;
